import { PageQuery, Paginate } from '@business/entities/common';
import { ContactId, ContactOrgProjectResponse, ContactRequest, ContactResponse } from '@business/entities/contact';
import { SendEmailRequest } from '@business/entities/gmail_messages';
import { OrgId, OrgResponseAdditional } from '@business/entities/organization';
import { RCDirection } from '@business/entities/ringcentral';
import { R } from '@core/utils/r';
import { Endpoint } from '../endpoint';
import { ContactActivityPageQuery, ContactActivityRequest, ContactActivityResponse } from './../../entities/contact/ContactActivityResponse';
import { CustomFieldService, CommonService } from '@business/services';
import { EmailTemplateModalData } from '@modules/common/EmailTemplateModal/EmailTemplateModal';

export function fetchContactsPaginated(params: PageQuery = {}) {
  return Endpoint.get<Paginate<ContactResponse, OrgResponseAdditional>>(`@gc/contacts/paginated`, {
    params: R.objectCompact(params, { number: true, string: true }),
  });
}

export function fetchContacts(options: { search?: string; orgId?: OrgId } = {}) {
  const params = R.objectCompact(options);
  return Endpoint.get<ContactResponse[]>(`@gc/contacts`, { params });
}

export function fetchContact(id: ContactId) {
  return Endpoint.get<ContactResponse>(`@gc/contacts/contact/${id}`);
}

export function fetchContactOrganization(id: ContactId) {
  return Endpoint.get<ContactOrgProjectResponse>(`@gc/contacts/contact-organization/${id}`);
}

export function createContact(data: ContactRequest) {
  if (CustomFieldService.hasFile(data.customFields)) {
    const formData = CommonService.toFormData(data);
    return Endpoint.post<ContactResponse>(`@gc/contacts/create`, { formData, hasFiles: true });
  }
  return Endpoint.post<ContactResponse>(`@gc/contacts/create`, { data });
}

export function updateContact(id: ContactId, data: ContactRequest) {
  if (CustomFieldService.hasFile(data.customFields)) {
    const formData = CommonService.toFormData(data);
    return Endpoint.post<ContactResponse>(`@gc/contacts/update/${id}`, { formData, hasFiles: true });
  }
  return Endpoint.post<ContactResponse>(`@gc/contacts/update/${id}`, { data });
}

export function deleteContact(contactId: ContactId, removeEmails: boolean) {
  return Endpoint.delete<void>(`@gc/contacts/${contactId}`, { data: { removeEmails } });
}

/* Activity Endpoitns */

export function fetchActivities(options: ContactActivityPageQuery) {
  const params = R.objectCompact(options);
  return Endpoint.get<ContactActivityResponse[]>(`@gc/contacts/activity`, { params });
}

export function fetchPaginateActivities(options: ContactActivityPageQuery) {
  const params = R.objectCompact(options);
  return Endpoint.get<Paginate<ContactActivityResponse>>(`@gc/contacts/activity`, { params });
}

export function createActivity(data: ContactActivityRequest) {
  return Endpoint.post<ContactActivityResponse>(`@gc/contacts/activity`, { data });
}

export function updateActivity(id: string, data: Partial<ContactActivityRequest>) {
  return Endpoint.patch<ContactActivityResponse>(`@gc/contacts/activity/${id}`, { data: { ...data } });
}

export function onTogglePin(id: string) {
  return Endpoint.patch<ContactActivityResponse>(`@gc/contacts/activity/toggle-pin/${id}`);
}

export function onTogglePinEmail(id: string) {
  return Endpoint.patch<void>(`@gc/contacts/activity/toggle-pin-email/${id}`);
}

export function deleteActivity(id: string) {
  return Endpoint.delete<void>(`@gc/contacts/activity/${id}`);
}

export function getDataByPhone(phone: string) {
  return Endpoint.get<ContactResponse>(`@gc/contacts/data-by-phone`, { params: { phone } });
}

export function addCallLog(callId: string, phone: string, direction: RCDirection) {
  return Endpoint.post<void>(`@gc/contacts/call-log`, { data: { callId, phone, direction } });
}

export function sendEmail(data: SendEmailRequest) {
  return Endpoint.post<ContactActivityResponse>(`@gc/mail`, { data });
}

export function trackSMS(from: string, to: string, direction: RCDirection, content: string) {
  return Endpoint.post<void>(`@gc/contacts/track-sms`, { data: { from, to, content, direction } });
}

export function trackCall(id: string, from: string, to: string, direction: RCDirection, start: number, end: number) {
  return Endpoint.post<void>(`@gc/contacts/track-call`, { data: { from, to, direction, start, end, id } });
}

export function sendGenericEmail(data: EmailTemplateModalData) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<void>(`@gc/contacts/generic-email`, { formData, hasFiles: true });
}
